import { defineMessages, type IntlShape } from "react-intl";
import { publishableInstagramPostTypeSchema } from "@runcheetah/shared";

import { isInstagramPublicationPending } from "@/lib/instagram-publication";

export type CalendarPostOwner =
  | { type: "campaign"; id: string; name: string }
  | { type: "standalone"; id: string; name: string };

export type CalendarPostLike = {
  id: string;
  scheduledFor: string;
  status: string;
  approvalStatus: "pending" | "approved" | "rejected";
  owner: CalendarPostOwner;
  slot?: {
    platform?: string;
    postType?: string;
  };
  instagramConnection?: { isConnected: boolean } | null;
  publication?: {
    status?: string | null;
    publishNowRequestedAt?: string | null;
    publishedAt?: string | null;
  } | null;
};

export type CalendarEventLike = {
  id: string;
  date: string;
  countryCode: string;
  name: string;
  localName: string;
};

export type CalendarDay = {
  key: string;
  date: Date;
};

export type CalendarPostGroup<TPost extends CalendarPostLike> = {
  key: string;
  label: string;
  owner: CalendarPostOwner;
  posts: TPost[];
};

export type CalendarDayGroup<TPost extends CalendarPostLike> = CalendarDay & {
  groups: CalendarPostGroup<TPost>[];
};

const calendarPostMessages = defineMessages({
  standalone: {
    id: "calendarPost.owner.standalone",
    defaultMessage: "Standalone",
  },
  approved: {
    id: "calendarPost.approval.approved",
    defaultMessage: "Approved",
  },
  rejected: {
    id: "calendarPost.approval.rejected",
    defaultMessage: "Rejected",
  },
  needsApproval: {
    id: "calendarPost.approval.needsApproval",
    defaultMessage: "Needs approval",
  },
  pendingRender: {
    id: "calendarPost.approval.pendingRender",
    defaultMessage: "Pending render",
  },
});

export const toCalendarDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getCalendarWindowStart = (date: Date): Date => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
};

export const addCalendarDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

export const getCalendarWindowDays = (windowStart: Date): CalendarDay[] =>
  Array.from({ length: 7 }, (_, index) => {
    const date = addCalendarDays(windowStart, index);
    return { key: toCalendarDateKey(date), date };
  });

export const getCalendarRange = (windowStart: Date) => ({
  start: windowStart.toISOString(),
  end: addCalendarDays(windowStart, 7).toISOString(),
});

export const getCalendarEventRange = (windowStart: Date) => ({
  startDate: toCalendarDateKey(windowStart),
  endDate: toCalendarDateKey(addCalendarDays(windowStart, 7)),
});

export const isCalendarDayInPast = (date: Date, today = new Date()): boolean =>
  getCalendarWindowStart(date).getTime() < getCalendarWindowStart(today).getTime();

export const getCalendarEventPreviewItems = <TEvent>(
  events: TEvent[],
  maxVisible = 2,
): { visibleEvents: TEvent[]; hiddenCount: number } => {
  const visibleCount = Math.max(0, maxVisible);

  return {
    visibleEvents: events.slice(0, visibleCount),
    hiddenCount: Math.max(0, events.length - visibleCount),
  };
};

export const getStandaloneCalendarGroupLabel = (intl: IntlShape): string =>
  intl.formatMessage(calendarPostMessages.standalone);

export const shortenCalendarGroupName = (name: string, maxLength = 28, fallback = ""): string => {
  const normalized = name.trim().replace(/\s+/gu, " ");
  if (!normalized) {
    return fallback;
  }

  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
};

export const getCalendarPostEditPath = (brandId: string, post: CalendarPostLike): string => {
  if (post.owner.type === "campaign") {
    return `/app/brands/${brandId}/campaigns/${post.owner.id}/posts/${post.id}`;
  }

  return `/app/brands/${brandId}/posts/batches/${post.owner.id}/posts/${post.id}`;
};

export const isCalendarPostUnapproved = (post: CalendarPostLike): boolean =>
  post.status === "generated" && post.approvalStatus !== "approved";

export const isCalendarPostVisible = (post: CalendarPostLike): boolean =>
  post.status === "generated";

export const canOpenCalendarPostEditor = (post: CalendarPostLike): boolean =>
  isCalendarPostVisible(post) && post.approvalStatus !== "approved";

export const getCalendarApprovalLabel = (post: CalendarPostLike, intl: IntlShape): string => {
  if (post.approvalStatus === "approved") {
    return intl.formatMessage(calendarPostMessages.approved);
  }

  if (post.approvalStatus === "rejected") {
    return intl.formatMessage(calendarPostMessages.rejected);
  }

  return intl.formatMessage(
    post.status === "generated"
      ? calendarPostMessages.needsApproval
      : calendarPostMessages.pendingRender,
  );
};

export const getCalendarPostReviewActions = (post: CalendarPostLike) => {
  const canReview = post.status === "generated";
  const canApproveStatus = canReview && post.approvalStatus !== "approved";
  const isApproved = canReview && post.approvalStatus === "approved";
  const platform = post.slot?.platform ?? "instagram";
  const postType = post.slot?.postType ?? null;
  const isInstagramFeedImage =
    platform === "instagram" && (postType === "feed_square" || postType === "feed_portrait");
  const isPublishableInstagramPost =
    platform === "instagram" && publishableInstagramPostTypeSchema.safeParse(postType).success;
  const isInstagramDisconnected = isPublishableInstagramPost
    ? post.instagramConnection?.isConnected !== true
    : isInstagramFeedImage && post.instagramConnection?.isConnected === false;
  const publishPending = isInstagramPublicationPending(post.publication);
  const showPublishNow =
    isApproved &&
    isPublishableInstagramPost &&
    !isInstagramDisconnected &&
    post.publication?.status !== "published";
  const primaryAction = isApproved
    ? isInstagramDisconnected
      ? "connect_instagram"
      : showPublishNow
        ? "publish_now"
        : null
    : !canApproveStatus
      ? null
      : isInstagramDisconnected
        ? "connect_instagram"
        : isInstagramFeedImage
          ? "approve"
          : "download";

  return {
    canApprove: primaryAction === "approve",
    canConnectInstagram: primaryAction === "connect_instagram",
    canDownload: primaryAction === "download",
    canPublishNow: showPublishNow && !publishPending,
    canReject: canReview && post.approvalStatus !== "rejected",
    publishPending,
    showPublishNow,
    primaryAction,
  };
};

export const groupCalendarPostsByDay = <TPost extends CalendarPostLike>(
  days: CalendarDay[],
  posts: TPost[],
  options: { standaloneLabel?: string } = {},
): CalendarDayGroup<TPost>[] => {
  const postsByDay = new Map<string, TPost[]>();
  for (const post of posts) {
    const key = toCalendarDateKey(new Date(post.scheduledFor));
    const list = postsByDay.get(key) ?? [];
    list.push(post);
    postsByDay.set(key, list);
  }

  return days.map((day) => {
    const dayPosts = (postsByDay.get(day.key) ?? []).toSorted(
      (left, right) =>
        new Date(left.scheduledFor).getTime() - new Date(right.scheduledFor).getTime(),
    );
    const groupsByKey = new Map<string, CalendarPostGroup<TPost>>();

    for (const post of dayPosts) {
      const key = `${post.owner.type}:${post.owner.id}`;
      const existing = groupsByKey.get(key);
      if (existing) {
        existing.posts.push(post);
        continue;
      }

      groupsByKey.set(key, {
        key,
        label:
          post.owner.type === "standalone"
            ? (options.standaloneLabel ?? "")
            : shortenCalendarGroupName(post.owner.name),
        owner: post.owner,
        posts: [post],
      });
    }

    return { ...day, groups: Array.from(groupsByKey.values()) };
  });
};

export const groupCalendarEventsByDay = <TEvent extends CalendarEventLike>(
  days: CalendarDay[],
  events: TEvent[],
): Map<string, TEvent[]> => {
  const dayKeys = new Set(days.map((day) => day.key));
  const eventsByDay = new Map<string, TEvent[]>();

  for (const event of events) {
    if (!dayKeys.has(event.date)) {
      continue;
    }

    const list = eventsByDay.get(event.date) ?? [];
    list.push(event);
    eventsByDay.set(event.date, list);
  }

  for (const [key, list] of eventsByDay.entries()) {
    eventsByDay.set(
      key,
      list.toSorted(
        (left, right) =>
          left.countryCode.localeCompare(right.countryCode) ||
          left.name.localeCompare(right.name) ||
          left.localName.localeCompare(right.localName),
      ),
    );
  }

  return eventsByDay;
};

export const countCalendarPosts = <TPost extends CalendarPostLike>(
  days: CalendarDayGroup<TPost>[],
) =>
  days.reduce(
    (total, day) =>
      total + day.groups.reduce((dayTotal, group) => dayTotal + group.posts.length, 0),
    0,
  );
